import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import SunCalc from "suncalc";

const initialState = {
  currentWeather: null,
  forecast: [],
  status: "idle",
  error: null,
  location: "helsinki,fi",
};

const generateWeatherAlerts = (weatherData, forecastData) => {
  const alerts = [];
  const currentTemp = weatherData.main.temp;
  const currentWind = weatherData.wind.speed;
  const currentWeatherMain = weatherData.weather[0]?.main?.toLowerCase();

  // Temperature alerts
  if (currentTemp >= 35) {
    alerts.push({
      event: "Extreme Heat Warning",
      description:
        "Temperature is above 35°C. Take precautions against heat exposure.",
      severity: "severe",
      start: Math.floor(Date.now() / 1000),
      end: Math.floor(Date.now() / 1000) + 24 * 3600, // 24 hours from now
      sender_name: "Weather Service",
    });
  } else if (currentTemp <= 0) {
    alerts.push({
      event: "Freezing Temperature Alert",
      description:
        "Temperature is at or below freezing point. Be aware of icy conditions.",
      severity: "moderate",
      start: Math.floor(Date.now() / 1000),
      end: Math.floor(Date.now() / 1000) + 24 * 3600,
      sender_name: "Weather Service",
    });
  }

  // Wind alerts
  if (currentWind >= 10.8) {
    // Strong breeze or higher on Beaufort scale
    alerts.push({
      event: "Strong Wind Alert",
      description: "Strong winds may affect outdoor activities and travel.",
      severity: "moderate",
      start: Math.floor(Date.now() / 1000),
      end: Math.floor(Date.now() / 1000) + 12 * 3600, // 12 hours from now
      sender_name: "Weather Service",
    });
  }

  // Severe weather alerts
  if (currentWeatherMain.includes("thunderstorm")) {
    alerts.push({
      event: "Thunderstorm Warning",
      description:
        "Thunderstorm activity detected in your area. Seek shelter if necessary.",
      severity: "severe",
      start: Math.floor(Date.now() / 1000),
      end: Math.floor(Date.now() / 1000) + 6 * 3600, // 6 hours from now
      sender_name: "Weather Service",
    });
  }

  // Check forecast for upcoming severe weather
  if (forecastData.list && forecastData.list.length > 0) {
    const next24Hours = forecastData.list.slice(0, 8); // Next 24 hours (8 x 3-hour intervals)
    const willRain = next24Hours.some(
      (item) =>
        item.weather[0]?.main?.toLowerCase().includes("rain") ||
        item.weather[0]?.main?.toLowerCase().includes("thunderstorm")
    );

    if (willRain) {
      alerts.push({
        event: "Rain Expected",
        description: "Rain is expected in the next 24 hours. Plan accordingly.",
        severity: "minor",
        start: Math.floor(Date.now() / 1000),
        end: Math.floor(Date.now() / 1000) + 24 * 3600,
        sender_name: "Weather Service",
      });
    }
  }

  return alerts;
};

export const fetchWeatherData = createAsyncThunk(
  "weather/fetchWeatherData",
  async (_, { getState }) => {
    const state = getState();
    const location = state.weather.location;

    // Fetch current weather
    const currentResponse = await fetch(
      `https://api.openweathermap.org/data/2.5/weather?q=${location}&appid=${process.env.REACT_APP_OPEN_WEATHER_API_LKEY}&units=metric`
    );
    const currentData = await currentResponse.json();
    if (!currentResponse.ok) {
      throw new Error(currentData.message);
    }

    const { lat, lon } = currentData.coord;

    // Fetch air quality data
    const airQualityResponse = await fetch(
      `https://api.openweathermap.org/data/2.5/air_pollution?lat=${lat}&lon=${lon}&appid=${process.env.REACT_APP_OPEN_WEATHER_API_LKEY}`
    );
    const airQualityData = await airQualityResponse.json();
    if (!airQualityResponse.ok) {
      throw new Error(airQualityData.message);
    }

    // Fetch 5-day forecast
    const forecastResponse = await fetch(
      `https://api.openweathermap.org/data/2.5/forecast?q=${location}&appid=${process.env.REACT_APP_OPEN_WEATHER_API_LKEY}&units=metric`
    );
    const forecastData = await forecastResponse.json();
    if (!forecastResponse.ok) {
      throw new Error(forecastData.message);
    }

    // Generate alerts based on current conditions and forecast
    const alerts = generateWeatherAlerts(currentData, forecastData);

    // Estimate UV index based on time of day and cloud cover
    const estimateUVIndex = (cloudCover, hour) => {
      // Base UV index on a clear day at different times
      const baseUV = {
        early: 2,
        mid: 8,
        late: 2,
        night: 0,
      };

      // Determine time of day
      let timeOfDay = "night";
      if (hour >= 6 && hour < 10) timeOfDay = "early";
      else if (hour >= 10 && hour < 16) timeOfDay = "mid";
      else if (hour >= 16 && hour < 20) timeOfDay = "late";

      // Cloud cover reduction (0-100%)
      const cloudFactor = 1 - cloudCover / 100;

      return baseUV[timeOfDay] * cloudFactor;
    };

    const currentHour = new Date().getHours();
    const cloudCover = currentData.clouds?.all || 0;
    const estimatedUVI = estimateUVIndex(cloudCover, currentHour);

    // Compute moon times and phase using SunCalc
    const now = new Date();
    const moonTimes = SunCalc.getMoonTimes(now, lat, lon);
    const moonIllum = SunCalc.getMoonIllumination(now);
    const moonrise = moonTimes.rise ? moonTimes.rise.getTime() / 1000 : null;
    const moonset = moonTimes.set ? moonTimes.set.getTime() / 1000 : null;
    const moonPhase = moonIllum.phase;

    return {
      currentWeather: {
        ...currentData,
        uvi: estimatedUVI,
        airQuality: {
          aqi: airQualityData.list[0].main.aqi,
          ...airQualityData.list[0].components,
        },
        alerts: alerts,
        moonrise,
        moonset,
        moonPhase,
      },
      forecast: forecastData.list,
    };
  }
);

const weatherSlice = createSlice({
  name: "weather",
  initialState,
  reducers: {
    setLocation: (state, action) => {
      state.location = action.payload;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWeatherData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchWeatherData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentWeather = action.payload.currentWeather;
        state.forecast = action.payload.forecast;
        state.error = null;
      })
      .addCase(fetchWeatherData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { setLocation } = weatherSlice.actions;
export default weatherSlice.reducer;
